import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, Input, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Customer } from 'src/app/models/customer.model';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { APIServiceService } from 'src/app/api/apiservice.service';
import { CustomerEditFormComponent } from '../customer-edit-form/customer-edit-form.component';
import { getCustomerInitials, stringToHexColor } from 'src/utils/display';
import { InstantSearchService } from 'src/app/services/instant-search.service';
import { connectHits, connectSearchBox } from 'instantsearch.js/es/connectors';
import { BaseHit } from 'instantsearch.js';
import { AlgoliaCustomerHit } from 'src/interfaces';

@Component({
  selector: 'ubc-customer-search',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, CustomerEditFormComponent],
  templateUrl: './customer-search.component.html',
  styleUrl: './customer-search.component.scss'
})
export class CustomerSearchComponent implements OnDestroy {

  public hits: BaseHit[] = [];
  public refine: (query: string) => void;
  public query: string;

  @Output() customerWasPicked: EventEmitter<Customer | undefined> = new EventEmitter<Customer | undefined>()
  @Output() resultsUpdated: EventEmitter<Customer[]> = new EventEmitter<Customer[]>()

  @Input() disableList = false

  creatingCustomer: boolean = false
  selectedCustomer?: Customer
  colors: string[] = [];
  newCustomerForm: FormGroup
  searchControl: FormControl = new FormControl('');

  options: Customer[] = []

  listOpen = true

  constructor(
    private apiService: APIServiceService,
    private InstantSearchService: InstantSearchService
  ){

    
    

  }

  ngAfterContentInit() {
    this.InstantSearchService.start();
  }

  public search(event: Event) {
    
    this.refine!((event.target as HTMLInputElement).value);
  }

  initSearchWidget(){

    this.query = ""
    this.hits = []

    this.InstantSearchService.removeAllListeners()

    const searchBox = ({refine, query}) => {
      this.refine = refine
      this.query = query
    }

    this.InstantSearchService.addWidgets([
      connectSearchBox(searchBox)({}),

      connectHits(({hits}) => {
        if(this.query.length > 0){
          this.hits = hits
        } else {
          this.hits = []
        }
        
        const customers = this.hits.map((hit) => {
          const customer = new Customer().deserialize(hit)
          customer.algoliaHit = hit as AlgoliaCustomerHit
          return customer
        })
        this.resultsUpdated.emit(customers)
      })({})
    ]);

  }

  ngOnDestroy(){
    console.log("On destrot")
    this.query = ""
    this.hits = []
  }

  ngOnInit(){
    // this.selectedCustomer = this.options[3]
    

    this.initSearchWidget()

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchForCustomer(term)
      .then(results => {
        this.options = results
        console.log(this.options)
      })
    })

    
  }

  getInitials(customer: Customer){
    return getCustomerInitials(customer)
  }

  stringToHexColor(str: string): string {
    return stringToHexColor(str)
  }

  // getInitials(customer: Customer){
  //   let out = ""

  //   if(customer.firstName){
  //     out += customer.firstName.charAt(0)
  //   }

  //   if(customer.lastName){
  //     out += customer.lastName.charAt(0)
  //   }

  //   return out
  // }

  // hashStringToNumber(str: string): number {
  //   let hash = 0;
  //   for (let i = 0; i < str.length; i++) {
  //     const char = str.charCodeAt(i);
  //     hash = (hash << 5) - hash + char;
  //     hash |= 0; // Convert to 32bit integer
  //   }
  //   return hash - 1000
  // }
  
  // stringToHexColor(str: string): string {
  //   const hash = this.hashStringToNumber(str);
  //   // Convert the hash number to a hex string
  //   let color = ((hash >> 24) & 0xFF).toString(16) +
  //               ((hash >> 16) & 0xFF).toString(16) +
  //               ((hash >> 8) & 0xFF).toString(16) +
  //               (hash & 0xFF).toString(16);
  //   // Ensure it's 6 characters long
  //   color = color.padStart(6, '0');
  //   // Return the hex color with a leading '#'
  //   return `#${color.substring(0, 6)}`;
  // }

  createNewCustomerClicked(){
    this.creatingCustomer = true
    this.newCustomerForm = new FormGroup({
      firstName: new FormControl('', Validators.required),
      lastName: new FormControl("", Validators.required),
      email: new FormControl("", [Validators.required, Validators.email]),
      tel: new FormControl("", Validators.required),
      billingAddress1: new FormControl(""),
      billingAddress2: new FormControl(""),
      billingTown: new FormControl(""),
      billingCity: new FormControl(""),
      billingPostcode: new FormControl(""),
      notes: new FormControl("")
    })
  }

  toggleList(){
    this.listOpen = !this.listOpen
  }

  createCustomerSubmit(){
    this.newCustomerForm = undefined
    this.creatingCustomer = false
  }

  async searchForCustomer(term: string): Promise<Customer[]>{

    if(term === ""){
      return []
    }

    try {
      const response = await this.apiService.customer.search(term.toLowerCase())
      if(!response.success || !response.data || response.data.results.length === 0){
        return []
      }

      this.listOpen = true

      return response.data.results.map((result) => {
        return new Customer().deserialize(result)
      })
    } catch(e){
      console.log(e)
      return []
    }

    const results = [
      new Customer().deserialize({ firstName: "Joel", lastName: "Spencer", id: 1, email: "test@test.com"}),
      new Customer().deserialize({ firstName: "Ruth", lastName: "Spencer", id: 2, email: "test@test.com"}),
      new Customer().deserialize({ firstName: "Ettie", lastName: "Spencer", id: 3, email: "test@test.com"}),
      new Customer().deserialize({ firstName: "Andy", lastName: "Goodwin", id: 4, email: "test@test.com"}),
      new Customer().deserialize({ firstName: "Graham", lastName: "Tennant", id: 5, email: "test@test.com"}),
      new Customer().deserialize({ firstName: "Muzzle", lastName: "Spencer", id: 6, email: "test@test.com"}),
      new Customer().deserialize({ firstName: "Boss", lastName: "Spencer", id: 7, email: "test@test.com"}),
      new Customer().deserialize({ firstName: "Ettie", lastName: "Spencer", id: 8, email: "test@test.com"}),
      new Customer().deserialize({ firstName: "Andy", lastName: "Goodwin", id: 9, email: "test@test.com"}),
      new Customer().deserialize({ firstName: "Graham", lastName: "Tennant", id: 10, email: "test@test.com"}),
      new Customer().deserialize({ firstName: "Muzzle", lastName: "Spencer", id: 11, email: "test@test.com"}),
      new Customer().deserialize({ firstName: "Boss", lastName: "Spencer", id: 12, email: "test@test.com"})
    ]

    if(term === ""){
      return []
    }

    this.listOpen = true

    return results.filter((customer) => {
      console.log(`Searching for customer ${term}`)
      return customer.firstName.toLowerCase().includes(term.toLowerCase())
    })
  }

  async customerPicked(result: any){
    
    const customerResponse = await this.apiService.customer.getByID(result.id)
    this.customerWasPicked.emit( customerResponse.data )
  }

  customerCreateCancelled(){
    this.selectedCustomer = undefined
    this.creatingCustomer = false
  }

  newCustomerCreated(customer: Customer){
    this.selectedCustomer = customer
    this.customerWasPicked.emit(customer)
  }
  

}
